import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { PillWithIcon } from '@els/els-react--pill';
import Breadcrumb from '@Services/Breadcrumb';
import { getLatestSummary } from './selectors';
import { isSummaryContextStale } from './hooks/useEntryStaleness';
import useSummaryAvailable from './hooks/useSummaryAvailable';
import { resolveSummaryChain } from './summaryContext';

import './InSummaryPillContainer.scss';

export function InSummaryPillContainer({ citation, databaseId, resultName, className, latestSummary }) {
  const activeBreadcrumb = useMemo(() => Breadcrumb.getBreadcrumbForDbByResultName(databaseId, resultName), [databaseId, resultName]);
  const { isSummaryAvailable } = useSummaryAvailable({ activeBreadcrumb });

  // AR-3.5, resolved: scheme-level matching is on the bare id — `referencedReactions[].id` for
  // reactions, `referencedCitations[].id` for documents. FR-3.6: a stale entry deactivates its
  // "in summary" tag on the results side, so only LIVE entries count.
  const isCitationInSummary = useMemo(() => {
    if (!isSummaryAvailable || !citation?.id) {
      return false;
    }

    if (!latestSummary || isSummaryContextStale(latestSummary, activeBreadcrumb)) {
      return false;
    }
    const referenced = latestSummary.summaryResponse?.referencedReactions?.length
      ? latestSummary.summaryResponse.referencedReactions
      : latestSummary.summaryResponse?.referencedCitations;
    return referenced?.some((reference) => String(reference?.id) === String(citation.id));
  }, [isSummaryAvailable, latestSummary, citation, activeBreadcrumb]);

  return isCitationInSummary ? (
    <PillWithIcon
      iconColor="text"
      iconProps={{ size: 'xs', sprite: 'AI' }}
      pillColor="extended-blue-1"
      textColor="text"
      className={cn('ai-search-in-summary-pill', className)}>
      In summary
    </PillWithIcon>
  ) : null;
}

const mapStateToProps = (state, { databaseId, resultName }) => {
  const activeBreadcrumb = Breadcrumb.getBreadcrumbForDbByResultName(databaseId, resultName);
  const { chainKey } = resolveSummaryChain(activeBreadcrumb, databaseId);

  return {
    latestSummary: getLatestSummary(state, chainKey),
  };
};

export default connect(mapStateToProps, null)(InSummaryPillContainer);
